import { Listbox, Transition } from '@headlessui/react'
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid'
import classNames from 'classnames'
import React, { Fragment, useEffect } from 'react'
import {
  Control,
  Controller,
  FieldArrayWithId,
  useFieldArray,
  UseFormRegister,
  useWatch,
} from 'react-hook-form'
import { AnyForm, NumberInput } from '../../../../../components/number-input'
import { AdminPresidentialChartFormValues } from '../../../../../forms/charts/admin/admin-chart-presidential-form'
import { PresidentialChartPartiesFormValues } from '../../../../../forms/charts/chart-presidential-form'
import { AdminManagePresidentialChartState } from '../../hooks/use-manage-presidential-chart'

interface PartyItemProps {
  key: string
  index: number
  party: FieldArrayWithId<AdminPresidentialChartFormValues, 'parties'>
  parties: FieldArrayWithId<AdminPresidentialChartFormValues, 'parties'>[]
  control: Control<AdminPresidentialChartFormValues>
  register: UseFormRegister<AdminPresidentialChartFormValues>
  mode: string
  state: AdminManagePresidentialChartState
}

const PartyItem = (props: PartyItemProps) => {
  const i = props.index
  const party = props.party
  const parties = props.parties
  const register = props.register
  const control = props.control
  const formMode = props.mode
  const state: AdminManagePresidentialChartState = props.state
  const {
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = state.form
  const {
    racesFieldArray: { fields: races },
  } = state

  const politicalParties = useFieldArray({
    control: props.state.form.control,
    name: 'races.0.politicalParties',
  })

  const watchCandidates = watch('races.0.candidates')

  console.log(getValues())

  useEffect(() => {
    if (party.name !== 'Non-Partisan') {
      setValue(`races.0.politicalParties.0.candidates`, watchCandidates)
    }
  }, [watchCandidates])

  return (
    <>
      <div className="relative flex w-full p-5 mb-8 bg-white rounded-md shadow-md">
        <div className="flex">
          <div className="flex flex-wrap gap-4">
            <div className="flex flex-col w-[240px]">
              <input
                {...register(`races.0.name`)}
                type="text"
                id="name"
                placeholder="Race Name"
                className={classNames('py-3 px-4 bg-[#E6EBF5] rounded-md', {
                  'outline outline-2 outline-red-500 focus:outline-red-500':
                    errors?.races?.[0]?.name,
                })}
              />
              <label
                htmlFor="name"
                className="text-[#757575] font-light text-sm mt-1"
              >
                {errors?.races?.[0] ? (
                  <span className="text-[#FF0000]">
                    {errors?.races?.[0]?.name?.message}
                  </span>
                ) : (
                  'Race Name'
                )}
              </label>
            </div>
            <div className="flex flex-col w-[240px]">
              <Controller
                control={control}
                name={`races.0.politicalParties.0.name`}
                render={({ field: { value, onChange } }) => (
                  <Listbox
                    value={value}
                    onChange={(e) => {
                      onChange(e)

                      const partyAbbv = parties.find(
                        (party: any) =>
                          party.name.toLowerCase() === e.toLowerCase()
                      )?.abbv

                      if (partyAbbv) {
                        setValue('races.0.politicalParties.0.abbv', partyAbbv)
                      } else {
                        setValue('races.0.politicalParties.0.abbv', e)
                        setValue('races.0.raceType', e)
                      }
                    }}
                  >
                    <div className="relative h-[48px]">
                      <Listbox.Button
                        id="type"
                        className={`relative w-full h-full py-3 px-4 bg-[#E6EBF5] rounded-md text-left cursor-default ${
                          errors?.races &&
                          errors?.races?.[0]?.politicalParties?.[0]?.name &&
                          'border border-red-500 focus:outline-red-500'
                        }`}
                      >
                        <span className="block truncate">{value}</span>
                        <span className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                          <ChevronUpDownIcon
                            className="w-5 h-5 text-gray-400"
                            aria-hidden="true"
                          />
                        </span>
                      </Listbox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute z-10 w-full py-1 mt-1 overflow-auto text-base bg-white rounded-md shadow-lg max-h-60 ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                          {parties.map(
                            (
                              count: PresidentialChartPartiesFormValues,
                              i: number
                            ) => (
                              <Listbox.Option
                                key={count.id}
                                className={({ active }) =>
                                  `cursor-default select-none relative py-2 pl-10 pr-4 ${
                                    active
                                      ? 'text-blue-900 bg-blue-100'
                                      : 'text-gray-900'
                                  }`
                                }
                                value={count.name}
                              >
                                {({ selected }) => (
                                  <>
                                    <span
                                      className={`block truncate ${
                                        selected ? 'font-medium' : 'font-normal'
                                      }`}
                                    >
                                      {count.name}
                                    </span>
                                    {selected ? (
                                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                        <CheckIcon
                                          className="w-5 h-5"
                                          aria-hidden="true"
                                        />
                                      </span>
                                    ) : null}
                                  </>
                                )}
                              </Listbox.Option>
                            )
                          )}
                        </Listbox.Options>
                      </Transition>
                    </div>
                    <Listbox.Label className="text-[#757575] font-light text-sm mt-1">
                      Type
                    </Listbox.Label>
                  </Listbox>
                )}
              />
            </div>
            {parties[0]?.selected && (
              <>
                <div className="flex flex-wrap gap-4">
                  <>
                    <div className="w-[2px] bg-[#E6EBF5] h-12" />
                    <div className="flex flex-col w-[84px]">
                      <NumberInput
                        name={`races.0.candidates`}
                        register={register as UseFormRegister<AnyForm>}
                        id="candidates"
                        defaultValue={0}
                        disabled={getValues().showCandidateNames}
                        onChange={(e) =>
                          setValue(
                            'races.0.politicalParties.0.candidates',
                            +e.target.value
                          )
                        }
                        className="w-full h-12 focus:outline-none text-lg px-4 py-3 rounded-md bg-[#E6EBF5] leading-5 text-gray-900 focus:ring-0 disabled:opacity-25 disabled:cursor-not-allowed text-center"
                      />
                      <label
                        htmlFor="candidates"
                        className="text-[#757575] font-light text-sm mt-1"
                      >
                        Candidates
                      </label>
                    </div>
                  </>
                  <>
                    <div className="w-[2px] bg-[#E6EBF5] h-12" />
                    <div className="flex flex-col w-[84px]">
                      <NumberInput
                        name={`races.0.writeIns`}
                        register={register as UseFormRegister<AnyForm>}
                        id="writeIns"
                        defaultValue={1}
                        className="w-full h-12 focus:outline-none text-lg px-4 py-3 rounded-md bg-[#E6EBF5] leading-5 text-gray-900 focus:ring-0 disabled:opacity-25 disabled:cursor-not-allowed text-center"
                      />
                      <label
                        htmlFor="writeIns"
                        className="text-[#757575] font-light text-sm mt-1"
                      >
                        Write-Ins
                      </label>
                    </div>
                    <div className="w-[2px] bg-[#E6EBF5] h-12" />
                    <div className="flex flex-col w-[84px]">
                      <NumberInput
                        name={`races.0.voteFor`}
                        register={register as UseFormRegister<AnyForm>}
                        id="voteFor"
                        defaultValue={1}
                        className="w-full h-12 focus:outline-none text-lg px-4 py-3 rounded-md bg-[#E6EBF5] leading-5 text-gray-900 focus:ring-0 disabled:opacity-25 disabled:cursor-not-allowed text-center"
                      />
                      <label
                        htmlFor="voteFor"
                        className="text-[#757575] font-light text-sm mt-1"
                      >
                        Vote For
                      </label>
                    </div>
                  </>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

const AdminManagePresidentialChartParties: React.FC<{
  state: AdminManagePresidentialChartState
}> = ({ state }) => {
  const {
    addPoliticalParty,
    form: {
      register,
      control,
      formState: { errors },
      setValue,
      getValues,
    },
    mode: formMode,
    partiesFieldArray: { fields },
  } = state
  const parties = useWatch({ control, name: 'parties' })
  const prop = useWatch({ control, name: 'proposalOnly' })

  const [partyName, setPartyName] = React.useState('')
  const [partyAbbreviation, setPartyAbbreviation] = React.useState('')

  const addNewParty = () => {
    addPoliticalParty(partyName, partyAbbreviation)
    setPartyName('')
    setPartyAbbreviation('')
  }

  const partyCanBeAdded = () => {
    return partyName.length > 0 && partyAbbreviation.length > 0
  }

  return (
    <>
      <div className="mt-8 font-medium tracking-widest uppercase">Parties</div>
      <div className="container p-8 mx-auto my-2 border border-opacity-50 rounded-md border-primary-blue">
        <div className="flex gap-4">
          <input
            type="text"
            placeholder="New Party Name"
            id="newPartyName"
            className="w-64 px-4 py-3 border rounded-md border-primary-blue"
            value={partyName}
            onChange={(e) => setPartyName(e.target.value)}
          />
          <input
            type="text"
            placeholder="New Party Abbreviation"
            id="newPartyAbbreviation"
            className="w-64 px-4 py-3 border rounded-md border-primary-blue"
            value={partyAbbreviation}
            onChange={(e) => setPartyAbbreviation(e.target.value)}
          />
          <button
            className="uppercase tracking-[0.07rem] justify-center items-center px-6 py-3 text-primary-blue border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-transparent hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            disabled={!partyCanBeAdded()}
            onClick={() => {
              addNewParty()
            }}
          >
            Add Party
          </button>
          <button
            className="uppercase tracking-[0.07rem] justify-center items-center px-6 py-3 text-primary-blue border border-primary-blue text-base font-semibold rounded-md shadow-sm bg-transparent hover:bg-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
            onClick={(e) => {
              e.preventDefault()
              setValue('proposalOnly', !prop)
            }}
          >
            {getValues('proposalOnly') ? 'Enable Races' : 'Disable Races'}
          </button>
        </div>
        <div className="mt-8 font-bold">Select applicable party</div>
        <div className={`flex flex-wrap gap-2 mt-5 ${prop ? 'hidden' : null}`}>
          <PartyItem
            key={fields[0].id}
            index={0}
            party={fields[0]}
            parties={fields}
            control={control}
            register={register}
            mode={formMode}
            state={state}
          />
        </div>
      </div>
    </>
  )
}

export default AdminManagePresidentialChartParties
